import { useEffect, useRef, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import DedupePanel from "@/components/dashboard/DedupePanel";
import { X, Upload, Trash2, AlertCircle, CheckCircle2, Loader2, FileSpreadsheet, Award, Search, Pencil } from "lucide-react";

/**
 * CertificatesManager — admin tab for belt grading certificates.
 * Upload any format (.xlsx/.csv/.pdf/.docx/.image); same async pattern as results.
 */
export default function CertificatesManager() {
  const [list, setList] = useState({ total: 0, items: [] });
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [kyuOrDan, setKyuOrDan] = useState("");
  const [year, setYear] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [queue, setQueue] = useState([]); // [{name, status, parsed, inserted, error}]
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    const params = { limit: 100 };
    if (q) params.q = q;
    if (kyuOrDan) params.kyu_or_dan = kyuOrDan;
    if (year) params.year = year;
    try {
      const r = await api.get("/certificates", { params });
      setList(r.data || { total: 0, items: [] });
    } catch { setList({ total: 0, items: [] }); }
    try {
      const s = await api.get("/certificates/stats");
      setStats(s.data);
    } catch { setStats(null); }
  };

  useEffect(() => { refresh(); return () => clearInterval(pollRef.current); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(refresh, 200); return () => clearTimeout(t); }, [q, kyuOrDan, year]); // eslint-disable-line

  const pollJob = (jobId) => new Promise((resolve) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/certificates-jobs/${jobId}`);
        if (r.data.status === "done" || r.data.status === "failed") {
          clearInterval(pollRef.current);
          resolve(r.data);
        }
      } catch { /* keep polling */ }
    }, 4000);
  });

  const updateQueueItem = (idx, patch) => setQueue((q) => {
    const next = [...q];
    next[idx] = { ...next[idx], ...patch };
    return next;
  });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr(""); setOk(""); setBusy(true);
    const initial = files.map((f) => ({ name: f.name, status: "pending", parsed: 0, inserted: 0, error: null }));
    setQueue(initial);
    let totalParsed = 0, totalInserted = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      updateQueueItem(i, { status: "uploading" });
      const fd = new FormData();
      fd.append("file", f);
      const useReplace = i === 0 && replace; // only honour replace on the FIRST file of a batch
      try {
        const r = await api.post(
          `/certificates/import?replace=${useReplace ? "true" : "false"}`,
          fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
        );
        if (r.data.mode === "sync") {
          totalParsed += r.data.parsed; totalInserted += r.data.inserted;
          updateQueueItem(i, { status: "done", parsed: r.data.parsed, inserted: r.data.inserted });
        } else if (r.data.job_id) {
          updateQueueItem(i, { status: "running" });
          const final = await pollJob(r.data.job_id);
          if (final.status === "done") {
            totalParsed += final.parsed; totalInserted += final.inserted;
            updateQueueItem(i, { status: "done", parsed: final.parsed, inserted: final.inserted });
          } else {
            updateQueueItem(i, { status: "failed", error: final.error || "Parsing failed" });
          }
        }
      } catch (e2) {
        updateQueueItem(i, { status: "failed", error: formatApiError(e2.response?.data?.detail) || e2.message });
      }
    }
    setBusy(false);
    setOk(`Finished — Parsed ${totalParsed} rows, inserted ${totalInserted} across ${files.length} file${files.length === 1 ? "" : "s"}.`);
    await refresh();
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeOne = async (id) => {
    if (!window.confirm("Remove this certificate record?")) return;
    try { await api.delete(`/certificates/${id}`); await refresh(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const [editing, setEditing] = useState(null); // {id, draft:{}} opens inline editor

  const openEditor = (c) => setEditing({ id: c.id, draft: {
    athlete_name: c.athlete_name || "",
    certificate_number: c.certificate_number || "",
    belt_rank: c.belt_rank || "",
    exam_year: c.exam_year || "",
    style: c.style || "",
    format: c.format || "",
    academy: c.academy || "",
    district: c.district || "",
    state: c.state || "",
  }});

  const saveEditor = async () => {
    if (!editing) return;
    try {
      const payload = {};
      Object.entries(editing.draft).forEach(([k, v]) => {
        if (v !== "" && v != null) payload[k] = k === "exam_year" ? Number(v) : v;
      });
      await api.patch(`/certificates/${editing.id}`, payload);
      setEditing(null);
      await refresh();
      setOk("Record updated.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear ALL certificates? This cannot be undone.")) return;
    try { await api.delete("/certificates"); await refresh(); setOk("All certificates cleared."); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="certificates-manager">
      <div className="flex items-center gap-2">
        <Award className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Belt Grading Certificates</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Upload belt grading / certificate records in any format — Excel, CSV, PDF, Word or image. Published on <span className="font-semibold">/certificates</span> with search and filters.
      </p>

      {stats && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="certificates-stats">
          <Stat label="Total Records" value={stats.total} />
          <Stat label="Dan Grades" value={stats.dan} accent />
          <Stat label="Kyu Grades" value={stats.kyu} />
          <Stat label="Years Covered" value={stats.years.length} />
        </div>
      )}

      <div className="mt-5 bg-slate-50 border border-slate-200 rounded-md p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="certificates-replace-toggle" />
            <span>Replace ALL existing records on upload</span>
          </label>
          <div className="flex-1" />
          {list.total > 0 && (
            <button onClick={clearAll} data-testid="certificates-clear-all"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-red-600 border border-red-200 bg-white px-3 py-2 rounded hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" /> Clear All
            </button>
          )}
        </div>
        <div className="mt-3">
          <input ref={fileRef} type="file" multiple onChange={onUpload} disabled={busy}
            accept=".xlsx,.xls,.csv,.pdf,.docx,.png,.jpg,.jpeg,.webp,.txt"
            data-testid="certificates-file-input"
            className="block w-full text-sm text-slate-600 file:mr-3 file:py-2.5 file:px-4 file:rounded file:border-0 file:bg-nkf-blue file:text-white file:text-xs file:uppercase file:tracking-widest file:font-bold hover:file:bg-nkf-navy disabled:opacity-50" />
          <div className="text-[11px] text-slate-500 mt-2">Pick ONE or MANY files at once — the AI will auto-sort rows, infer State/District/Academy from the title row and merge them all. Supported: .xlsx .xls .csv .pdf .docx .png .jpg.</div>
        </div>

        {queue.length > 0 && (
          <div className="mt-3 border border-slate-200 rounded bg-white max-h-64 overflow-y-auto" data-testid="certificates-import-queue">
            {queue.map((q, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 border-slate-100 text-sm">
                {q.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  : q.status === "failed" ? <AlertCircle className="h-4 w-4 text-red-600" />
                  : q.status === "pending" ? <FileSpreadsheet className="h-4 w-4 text-slate-400" />
                  : <Loader2 className="h-4 w-4 text-nkf-blue animate-spin" />}
                <span className="flex-1 min-w-0 truncate text-slate-700">{q.name}</span>
                <span className="text-xs text-slate-500 shrink-0">
                  {q.status === "done" ? `${q.inserted}/${q.parsed}`
                    : q.status === "failed" ? "failed"
                    : q.status === "running" ? "AI reading…"
                    : q.status === "uploading" ? "uploading…"
                    : "queued"}
                </span>
              </div>
            ))}
          </div>
        )}
        {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
        {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}
      </div>

      <DedupePanel onChange={refresh} />

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or certificate number…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue"
            data-testid="certificates-admin-search" />
        </div>
        <select value={kyuOrDan} onChange={(e) => setKyuOrDan(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="certificates-admin-grade">
          <option value="">All grades</option>
          <option value="dan">Dan (Black)</option>
          <option value="kyu">Kyu</option>
          <option value="color">Color only</option>
        </select>
        {stats && stats.years.length > 0 && (
          <select value={year} onChange={(e) => setYear(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="certificates-admin-year">
            <option value="">All years</option>
            {stats.years.map((y) => <option key={y.year} value={y.year}>{y.year} ({y.count})</option>)}
          </select>
        )}
      </div>

      <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">{list.total} record{list.total === 1 ? "" : "s"}</div>
      <div className="mt-2 max-h-[500px] overflow-y-auto border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Cert. No</th>
              <th className="text-left px-3 py-2">Belt</th>
              <th className="text-left px-3 py-2">Year</th>
              <th className="text-left px-3 py-2">Style</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.items.map((c) => (
              <tr key={c.id} data-testid={`certificates-row-${c.id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{c.athlete_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">{c.certificate_number || "—"}</td>
                <td className="px-3 py-2">
                  <BeltBadge rank={c.belt_rank} color={c.belt_color} kyuOrDan={c.kyu_or_dan} />
                </td>
                <td className="px-3 py-2 text-slate-600">{c.exam_year || "—"}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{c.style || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditor(c)} className="text-slate-500 hover:text-nkf-blue" data-testid={`certificates-edit-${c.id}`} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => removeOne(c.id)} className="text-red-500 hover:text-red-700" data-testid={`certificates-del-${c.id}`} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!list.items.length && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-sm">No certificates found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div role="dialog" aria-modal="true" onClick={() => setEditing(null)}
          className="fixed inset-0 z-50 bg-nkf-navy/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="certificates-edit-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-xl w-full max-w-xl my-10">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div className="font-display text-lg text-nkf-blue">Edit Certificate</div>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["athlete_name", "Athlete Name"],
                ["certificate_number", "Certificate No."],
                ["belt_rank", "Belt Rank"],
                ["exam_year", "Exam Year"],
                ["style", "Style"],
                ["academy", "Academy"],
                ["district", "District"],
                ["state", "State"],
              ].map(([k, label]) => (
                <label key={k} className="block" data-testid={`edit-field-${k}`}>
                  <span className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</span>
                  <input value={editing.draft[k]} onChange={(e) => setEditing((s) => ({ ...s, draft: { ...s.draft, [k]: e.target.value }}))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
                </label>
              ))}
              <label className="block md:col-span-2" data-testid="edit-field-format">
                <span className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Format</span>
                <select value={editing.draft.format} onChange={(e) => setEditing((s) => ({ ...s, draft: { ...s.draft, format: e.target.value }}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                  <option value="">— not specified —</option>
                  <option value="Traditional Karate">Traditional Karate</option>
                  <option value="Non Contact Karate">Non Contact Karate</option>
                  <option value="Semi Contact Karate">Semi Contact Karate</option>
                  <option value="Full Contact Karate">Full Contact Karate</option>
                </select>
              </label>
            </div>
            <div className="border-t border-slate-200 p-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={saveEditor} data-testid="edit-save" className="text-xs uppercase tracking-widest font-bold px-4 py-2 rounded bg-nkf-blue text-white hover:bg-nkf-navy">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border rounded px-3 py-2 ${accent ? "bg-nkf-gold/10 border-nkf-gold/30" : "bg-white border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-xl tabular-nums ${accent ? "text-nkf-red" : "text-nkf-blue"}`}>{value}</div>
    </div>
  );
}

function BeltBadge({ rank, color, kyuOrDan }) {
  const palette = {
    white: "bg-slate-50 text-slate-700 border-slate-300",
    yellow: "bg-yellow-100 text-yellow-900 border-yellow-300",
    orange: "bg-orange-100 text-orange-900 border-orange-300",
    green: "bg-emerald-100 text-emerald-900 border-emerald-300",
    blue: "bg-sky-100 text-sky-900 border-sky-300",
    purple: "bg-purple-100 text-purple-900 border-purple-300",
    brown: "bg-amber-100 text-amber-900 border-amber-400",
    red: "bg-red-100 text-red-900 border-red-300",
    black: "bg-slate-800 text-nkf-gold border-nkf-gold",
  };
  const cls = palette[color] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center text-[10.5px] uppercase tracking-widest font-bold border rounded px-2 py-0.5 ${cls}`}>
      {rank || color || kyuOrDan || "—"}
    </span>
  );
}
